/**
 * The two pucks a voice command is about, as data.
 *
 * `VoiceCommandModal.makeStageModel` (`:1278-1348`) already computes exactly
 * this, but it computes it as a component method: it reaches into
 * `this.$store` through `findStoredPuck`, so it can only be exercised by
 * mounting the modal, and the thing most likely to be wrong about it — which
 * puck is the source when the intent is `create_box`, which well is ringed for
 * a delete, whether a move is same-puck — is precisely the part that has no
 * pixels in it.
 *
 * This module is that logic with the store lifted out: the caller passes
 * `locate(label, id) -> located|null`, which the dock builds as a closure over
 * `puckLocator.locatePuck` and the store. Same fields out, plus the `source`
 * and `destination` descriptors the focus view draws its two columns from.
 *
 * The coordinates below are moved here, not re-derived. Copies remain in
 * `VoiceCommandModal.vue` until a later packet deletes the animated stage; the
 * two must not be allowed to drift, which is why this file quotes the same
 * provenance the modal does rather than restating it in its own words.
 */

import { breadcrumb } from "./puckLocator.js";

// Copied verbatim from RackSection's `wellCoordinates`, in the same
// 407.54 x 435.08 canvas — not re-derived, not idealised. Four rows of
// 2 / 3 / 4 / 3, numbered left to right and top to bottom. A puck is round, so
// the tempting thing to draw is a clock face; that would put well 7 somewhere
// the user has never seen it.
export const PUCK_WELL_COORDINATES = [
  { x: 111, y: 47 },
  { x: 213, y: 47 },

  { x: 63, y: 132 },
  { x: 162, y: 132 },
  { x: 261, y: 132 },

  { x: 18, y: 218 },
  { x: 114, y: 218 },
  { x: 210, y: 218 },
  { x: 306, y: 218 },

  { x: 63, y: 303 },
  { x: 162, y: 303 },
  { x: 261, y: 303 },
];

// The renderer's own slot too: a box glyph fills 83 x 84 at the raw coordinate.
export const PUCK_WELL_SLOT_W = 83;
export const PUCK_WELL_SLOT_H = 84;

/**
 * One entry per well, carrying what the puck renderer needs: the same
 * truthiness test for occupancy, the same box type, the same colour slug that
 * the app's generated `well-<colour>-clipped` rules key off (built in
 * `LoggedinHome.vue:430-455`), and the same four grid names.
 *
 * A null puck still yields twelve entries, all unfilled. The renderer must then
 * say so in words — see the note on `known` in `makeFocusModel` — because a
 * puck drawn with twelve empty recesses is a claim about someone's samples.
 */
export function wellsForPuck(puck) {
  return PUCK_WELL_COORDINATES.map((at, index) => {
    const n = index + 1;
    const box = puck ? puck["well" + n] : null;
    return {
      n,
      x: at.x,
      y: at.y,
      filled: !!box,
      boxType: (box && box.boxType) || null,
      colorSlug: String((box && box.color) || "blue").replace(/\s+/g, "-"),
      gridClips: [
        box?.gridTopLeft?.name,
        box?.gridTopRight?.name,
        box?.gridBottomLeft?.name,
        box?.gridBottomRight?.name,
      ],
    };
  });
}

/**
 * The raw coordinate of well 1-12. Out-of-range falls back to well 1 rather
 * than to undefined: a ring drawn in the wrong well is visibly wrong, a ring
 * drawn at NaN silently disappears.
 */
export function wellCoordinate(well) {
  return PUCK_WELL_COORDINATES[Number(well) - 1] || PUCK_WELL_COORDINATES[0];
}

/**
 * Whether "Show in rack" can find this puck.
 *
 * `known` and `inRack` are different facts and the view needs both. The locator
 * searches racks AND the unassigned pool, so a spare puck comes back known: its
 * wells are drawn, its breadcrumb reads "Unassigned". But the navigation the
 * button triggers — `ManageProducts.startAutoNavigate` — walks labs, dewars,
 * racks and pucks and has no unassigned branch at all, so on that puck it can
 * only fall through to "No puck found with that serial number." A button that
 * is always going to say that should not be drawn.
 */
const isInRack = (located) => !!(located && located.rack && located.dewar);

/**
 * The puck's colour as the store spells it — "sky blue", "gold", "chrome" — or
 * null when there is no puck to ask.
 *
 * Raw on purpose. The hex is `$store.state.transformPuckColor`'s to decide, and
 * that function lives in the store because the grid, the transport list and the
 * dock must all answer the same way; a second copy of the switch statement in
 * here is how the dock ends up drawing a puck the page behind draws differently.
 * A located puck with no colour at all keeps its null and still comes back
 * `known`, so the view can hand null to `transformPuckColor` and get the same
 * black the rack card gets for the same puck.
 */
function puckColor(puck) {
  const raw = puck && puck.color;
  if (raw == null) return null;
  const text = String(raw).trim();
  return text || null;
}

/** The descriptor one column of the focus view is drawn from. */
function facet(label, located, puck, well) {
  return {
    label: label || "Puck",
    breadcrumb: breadcrumb(located),
    known: !!puck,
    inRack: isInRack(located),
    well: well == null || Number.isNaN(well) ? null : well,
    color: puckColor(puck),
  };
}

/**
 * The line printed under a column: which well the box leaves, which well it
 * lands in, and — for a delete — that there is no landing.
 *
 * Pure and separate from the view because it is the sentence the user reads
 * instead of hunting for a dashed ring, and because the four intents phrase it
 * four different ways. A same-puck move says both wells in one line under the
 * one puck it draws; a cross-puck move splits the line between the two columns
 * it draws. Getting that backwards prints "To well 5" under a puck the box is
 * leaving.
 *
 * @param {object} model `makeFocusModel`'s return value.
 * @returns {{source: string|null, destination: string|null, danger: boolean}}
 *          `destination` is null whenever the view draws a single column.
 */
export function wellCaption(model) {
  const it = model || {};
  const from = it.sourceWell;
  const to = it.destinationWell;
  const none = { source: null, destination: null, danger: false };

  if (it.intent === "delete_box") {
    return from ? { source: `Well ${from}`, destination: null, danger: true } : none;
  }
  if (it.intent === "create_box") {
    return to
      ? { source: `Into well ${to}`, destination: null, danger: false }
      : none;
  }
  if (it.crossPuck) {
    return {
      source: from ? `From well ${from}` : null,
      destination: to ? `To well ${to}` : null,
      danger: false,
    };
  }
  // One puck, both wells — and the half-known case says the half it knows
  // rather than an arrow with nothing on one side of it.
  if (from && to) {
    return { source: `Well ${from} → well ${to}`, destination: null, danger: false };
  }
  if (from) return { source: `From well ${from}`, destination: null, danger: false };
  if (to) return { source: `To well ${to}`, destination: null, danger: false };
  return none;
}

/**
 * Build the focus model.
 *
 * @param {object} preview  the server's preview, read through `.resolved`
 * @param {string} intent   `move_box` | `delete_box` | `create_box`
 * @param {(label: string, id: string) => object|null} locate
 *        injected puck lookup; returns `puckLocator.locatePuck`'s shape.
 */
export function makeFocusModel(preview, intent, locate) {
  const resolved = (preview && preview.resolved) || {};
  const find = typeof locate === "function" ? locate : () => null;
  const isMove = intent === "move_box";
  const isDelete = intent === "delete_box";

  // Which puck is "primary" depends on the verb, and the three verbs disagree.
  // A move starts at its source; a delete has only one puck; a create has only
  // a destination, and drawing its ring on a source that does not exist is how
  // a create ends up looking like a move.
  const primaryLabel = isMove
    ? resolved.sourcePuckLabel
    : isDelete
      ? resolved.puckLabel
      : resolved.destinationPuckLabel;
  const primaryId = isMove
    ? resolved.sourcePuckId
    : isDelete
      ? resolved.puckId
      : resolved.destinationPuckId;
  const destinationLabel = isMove
    ? resolved.destinationPuckLabel
    : resolved.destinationPuckLabel || primaryLabel;
  const destinationId = isMove
    ? resolved.destinationPuckId
    : resolved.destinationPuckId || primaryId;
  const sourceWell = isMove
    ? Number(resolved.sourceWell)
    : isDelete
      ? Number(resolved.well)
      : null;
  const destinationWell = isMove
    ? Number(resolved.destinationWell)
    : intent === "create_box"
      ? Number(resolved.destinationWell)
      : null;

  // Ids win when both sides have them; labels are the fallback, compared the
  // way every other serial comparison in the app is.
  const samePuck =
    !isMove ||
    (primaryId && destinationId
      ? String(primaryId) === String(destinationId)
      : String(primaryLabel || "").trim().toUpperCase() ===
        String(destinationLabel || "").trim().toUpperCase());

  const primaryLocated = find(primaryLabel, primaryId);
  const destinationLocated = samePuck
    ? primaryLocated
    : find(destinationLabel, destinationId);
  const primaryPuck = (primaryLocated && primaryLocated.puck) || null;
  const destinationPuck =
    (destinationLocated && destinationLocated.puck) || null;

  const primaryWells = wellsForPuck(primaryPuck);
  const destinationWells = samePuck
    ? primaryWells
    : wellsForPuck(destinationPuck);
  const sourceEntry = sourceWell
    ? primaryWells.find((well) => well.n === sourceWell)
    : null;

  // What the box being moved or deleted looks like, so the ghost in the
  // destination well is that box rather than a generic rectangle. A create has
  // no source box, hence the defaults.
  const glyph = {
    boxType: (sourceEntry && sourceEntry.boxType) || "Clipped",
    colorSlug: (sourceEntry && sourceEntry.colorSlug) || "blue",
    gridClips: (sourceEntry && sourceEntry.gridClips) || [
      null,
      null,
      null,
      null,
    ],
  };

  return {
    intent,
    primaryLabel: primaryLabel || "Puck",
    destinationLabel: destinationLabel || primaryLabel || "Puck",
    primaryKnown: !!primaryPuck,
    destinationKnown: !!destinationPuck,
    // Alongside the facets for the same reason the `*Known` flags are: the view
    // builds its columns from the root.
    primaryColor: puckColor(primaryPuck),
    destinationColor: puckColor(destinationPuck),
    // Alongside the two `*Known` flags because the view builds its columns from
    // the root, not from the facets, and the button needs the pair.
    primaryInRack: isInRack(primaryLocated),
    destinationInRack: isInRack(destinationLocated),
    primaryWells,
    destinationWells,
    sourceWell,
    destinationWell,
    sourceCoordinate: sourceWell ? wellCoordinate(sourceWell) : null,
    destinationCoordinate: destinationWell
      ? wellCoordinate(destinationWell)
      : null,
    glyph,
    crossPuck: isMove && !samePuck,

    // The two columns. `source` is the puck a box leaves or is deleted from; a
    // create has nothing to leave, so its source IS the destination puck with
    // no well ringed. `destination` is null for a delete because a delete has
    // nowhere to land, and a column headed "destination" showing the same puck
    // would invite the reader to look for a box that is not going anywhere.
    source: facet(primaryLabel, primaryLocated, primaryPuck, sourceWell),
    destination: isDelete
      ? null
      : facet(
          destinationLabel,
          destinationLocated,
          destinationPuck,
          destinationWell
        ),
  };
}
